import React, { useEffect, useState } from 'react';

import { Box, Typography, Select, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useAppSelector, useAppDispatch } from 'hooks';
import { SensorManager, GroundStationManager } from 'models';
import { sensorsActions } from 'state/sensors';

import SensorPlot from 'components/sensors/SensorPlot';
import RefreshButton from 'components/RefreshButton';

const DEFAULT_UPDATE_TIME = 600_000; // 10 Minutes

const DEFAULT_HOURS_IN_PAST = 3;

const MILLIS_IN_HOUR = 3_600_000;

interface ListItemProps {
    groundStation: GroundStationManager.GroundStation;
    fieldIds?: SensorManager.SensorField['id'][];
    filters?: SensorManager.SensorRecordFilters;
}

const PlotGroup: React.FunctionComponent<ListItemProps> = ({
    fieldIds,
    filters,
    groundStation,
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const { list, fields, lastRefreshed, records } = useAppSelector(
        (root) => root.sensors,
    );

    const [hoursInPast, setHoursInPast] = useState(DEFAULT_HOURS_IN_PAST);

    const sensorsInGroundStation =
        list.object?.filter(
            (sensor) => sensor.groundStation === groundStation.id,
        ) ?? [];

    const fieldIdsInGroundStationSensors = sensorsInGroundStation.reduce<
        string[]
    >((acc, sensor) => {
        acc.push(...sensor.fields.map((conn) => conn.field));
        return acc;
    }, []);

    const fieldsToDisplay = fields.object
        ?.filter((field) => {
            // Filter out anything that isn't included in the overall fields filter
            if (fieldIds && !fieldIds.includes(field.id)) return false;

            return fieldIdsInGroundStationSensors.includes(field.id);
        })
        .reverse();

    const updateData = (field: SensorManager.SensorField, now = false) => {
        const previousRefresh = lastRefreshed[field.id] ?? 0;
        if (now || Date.now() - previousRefresh >= DEFAULT_UPDATE_TIME)
            dispatch(
                sensorsActions.getSensorRecords({
                    field: field.id,
                    after_time: new Date(
                        Date.now() - hoursInPast * MILLIS_IN_HOUR,
                    ).toISOString(),
                    ordering: 'time',
                    limit: 1000,
                    ...filters,
                }),
            );
    };

    const updateAllData = (now = false) => {
        fieldsToDisplay?.forEach((field) => {
            updateData(field, now);
        });
    };

    useEffect(() => {
        const updaterFn = setInterval(updateData, DEFAULT_UPDATE_TIME);

        updateAllData();

        return () => {
            clearInterval(updaterFn);
        };
    }, [hoursInPast]);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto 1fr',
                gap: 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <Box>
                    <Typography variant="h6">Sensor Data</Typography>
                    <Typography variant="subtitle1">
                        Getting Data from{' '}
                        {sensorsInGroundStation
                            .map((sensor) => sensor.name)
                            .join(', ')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                    }}
                >
                    <Select
                        value={hoursInPast}
                        onChange={(event) => {
                            const newValue = Number.parseFloat(
                                `${event.target.value}`,
                            );
                            if (!Number.isNaN(newValue))
                                setHoursInPast(newValue);
                        }}
                    >
                        <MenuItem value={0.5}>30 min</MenuItem>
                        <MenuItem value={1}>1 hour</MenuItem>
                        <MenuItem value={2}>2 hours</MenuItem>
                        <MenuItem value={3}>3 hours</MenuItem>
                        <MenuItem value={4}>4 hours</MenuItem>
                        <MenuItem value={6}>6 hours</MenuItem>
                        <MenuItem value={12}>12 hours</MenuItem>
                        <MenuItem value={24}>24 hours</MenuItem>
                    </Select>
                    <RefreshButton
                        isRefreshing={records.status === 'loading'}
                        onClick={() => updateAllData(true)}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gap: 1,
                    [theme.breakpoints.down('xl')]: {
                        gridTemplateRows: 'auto auto',
                    },
                    [theme.breakpoints.up('xl')]: {
                        gridTemplateColumns: '1fr 1fr',
                    },
                }}
            >
                {fieldsToDisplay?.map((field) => (
                    <SensorPlot
                        key={field.id}
                        field={field}
                        filters={filters}
                        height={300}
                    />
                ))}
            </Box>
        </Box>
    );
};

PlotGroup.displayName = 'PlotGroup';
export default PlotGroup;
